import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Note, Category } from '@voice/shared';

interface NotesListProps {
  notes: Note[];
  fromCache: boolean;
  onTogglePin: (noteId: string, pinned: boolean) => void;
  categories?: Category[];
}

export function NotesList({ notes, fromCache, onTogglePin, categories }: NotesListProps) {
  if (notes.length === 0) {
    return (
      <div style={styles.empty}>
        <div style={styles.emptyIcon}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity={0.5}>
            <rect x="9" y="1" width="6" height="12" rx="3" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </div>
        <p style={styles.emptyText}>No notes yet</p>
        <p style={styles.emptyHint}>Record your first voice note</p>
      </div>
    );
  }

  const pinned = notes.filter((n) => n.pinned);
  const unpinned = notes.filter((n) => !n.pinned);

  return (
    <div style={styles.list}>
      {/* Pinned section */}
      {pinned.length > 0 && (
        <>
          <div style={styles.sectionHeader}>
            <PinIcon size={12} filled />
            <span style={styles.sectionTitle}>Pinned</span>
          </div>
          {pinned.map((note) => (
            <NoteCard key={note.id} note={note} onTogglePin={onTogglePin} categories={categories} />
          ))}
        </>
      )}

      {/* Timeline section */}
      <div style={styles.sectionHeader}>
        <span style={styles.sectionTitle}>Timeline</span>
        {fromCache && <span style={styles.cacheBadge}>cached</span>}
      </div>
      {unpinned.length === 0 && pinned.length > 0 ? (
        <p style={styles.allPinnedHint}>All notes are pinned</p>
      ) : (
        unpinned.map((note) => (
          <NoteCard key={note.id} note={note} onTogglePin={onTogglePin} categories={categories} />
        ))
      )}
    </div>
  );
}

function getIntentIcon(intent: string): string {
  switch (intent) {
    case 'reminder': return '\u{1F514}';
    case 'task': return '\u2713';
    default: return '\u{1F4DD}';
  }
}

function getIntentColors(intent: string): { color: string; bg: string } {
  switch (intent) {
    case 'reminder':
      return { color: 'var(--color-warning)', bg: 'rgba(245, 158, 11, 0.1)' };
    case 'task':
      return { color: 'var(--color-success)', bg: 'rgba(34, 197, 94, 0.1)' };
    default:
      return { color: 'var(--color-accent)', bg: 'var(--color-accent-light)' };
  }
}

const NoteCard = memo(function NoteCard({
  note,
  onTogglePin,
  categories,
}: {
  note: Note;
  onTogglePin: (id: string, pinned: boolean) => void;
  categories?: Category[];
}) {
  const navigate = useNavigate();
  const isTransient = note.status === 'transcribing' || note.status === 'processing';
  const displayText = isTransient
    ? note.status === 'transcribing'
      ? 'Transcribing...'
      : 'Processing...'
    : (note.edited_transcript || note.raw_transcript || note.title || 'Untitled note');
  const truncated = displayText.length > 120 ? displayText.slice(0, 120) + '...' : displayText;
  const timeAgo = formatRelativeTime(note.created_at);
  const category = note.category_id && categories
    ? categories.find((c) => c.id === note.category_id)
    : undefined;
  const intentColors = note.intent ? getIntentColors(note.intent) : null;

  return (
    <div
      style={styles.card}
      onClick={() => navigate(`/notes/${note.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/notes/${note.id}`); }}
    >
      <div style={styles.cardTop}>
        <StatusDot status={note.status} />
        <span style={styles.cardTime}>{timeAgo}</span>
        {note.duration_seconds != null && (
          <span style={styles.cardDuration}>{note.duration_seconds}s</span>
        )}
        <button
          type="button"
          style={styles.pinBtn}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin(note.id, !note.pinned);
          }}
          aria-label={note.pinned ? 'Unpin note' : 'Pin note'}
        >
          <PinIcon size={14} filled={note.pinned} />
        </button>
      </div>

      {/* Intent + category meta row */}
      {(note.intent || category) && (
        <div style={styles.metaRow}>
          {note.intent && intentColors && (
            <span style={{
              ...styles.intentChip,
              color: intentColors.color,
              backgroundColor: intentColors.bg,
            }}>
              <span style={styles.intentIcon}>{getIntentIcon(note.intent)}</span>
              {note.intent}
            </span>
          )}
          {category && (
            <span style={{
              ...styles.categoryTag,
              borderColor: category.color ?? 'var(--color-border)',
              color: category.color ?? 'var(--color-text-secondary)',
            }}>
              <span style={{
                ...styles.categoryDot,
                backgroundColor: category.color ?? 'var(--color-text-muted)',
              }} />
              {category.name}
            </span>
          )}
          {note.confidence != null && note.confidence >= 0.60 && note.confidence < 0.80 && (
            <span style={styles.reviewBadge}>Review</span>
          )}
        </div>
      )}

      {note.summary && <p style={styles.cardSummary}>{note.summary}</p>}
      <p style={styles.cardTranscript}>{truncated}</p>
    </div>
  );
});

function PinIcon({ size = 14, filled = false }: { size?: number; filled?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'var(--color-pin)' : 'none'}
      stroke={filled ? 'var(--color-pin)' : 'var(--color-text-muted)'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 17v5" />
      <path d="M9 2h6l-1 7h4l-5 6H9l-1-6H4l5-7z" />
    </svg>
  );
}

function StatusDot({ status }: { status: string }) {
  const color =
    status === 'ready'
      ? 'var(--color-success)'
      : status === 'error'
        ? 'var(--color-danger)'
        : status === 'processing' || status === 'transcribing'
          ? 'var(--color-accent)'
          : 'var(--color-text-muted)';

  return (
    <span
      style={{
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
  );
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const styles = {
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-2)',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    paddingTop: 'var(--space-2)',
    paddingBottom: 'var(--space-1)',
  },
  sectionTitle: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    flex: 1,
  },
  cacheBadge: {
    fontSize: '0.625rem',
    color: 'var(--color-text-muted)',
    padding: '1px 6px',
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)',
  },
  allPinnedHint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    textAlign: 'center' as const,
    padding: 'var(--space-4)',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-10) var(--space-4)',
  },
  emptyIcon: {
    marginBottom: 'var(--space-1)',
  },
  emptyText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    fontWeight: 500,
  },
  emptyHint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
  },
  card: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-2)',
    padding: 'var(--space-3)',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    cursor: 'pointer',
    transition: 'border-color var(--transition-fast)',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
  },
  cardTime: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    flex: 1,
  },
  cardDuration: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
  },
  pinBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    opacity: 0.7,
    transition: 'opacity var(--transition-fast)',
  },
  cardSummary: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
    lineHeight: 1.4,
  },
  cardTranscript: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.5,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-1)',
    flexWrap: 'wrap' as const,
  },
  intentChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 'var(--radius-full)',
    textTransform: 'capitalize' as const,
    letterSpacing: '0.02em',
  },
  intentIcon: {
    fontSize: '0.7rem',
    lineHeight: 1,
  },
  categoryTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.625rem',
    fontWeight: 500,
    padding: '2px 8px',
    borderRadius: 'var(--radius-full)',
    border: '1px solid',
  },
  categoryDot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  reviewBadge: {
    alignSelf: 'flex-start' as const,
    fontSize: '0.625rem',
    color: 'var(--color-warning)',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-full)',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
};
